import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ApiToken } from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import Trans from '@lib/components/Trans/Trans';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import getStatusFromError from '@lib/utils/getStatusFromError';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import ApiTokenModalForm from 'routes/PublicApi/components/ApiTokenModalForm/ApiTokenModalForm';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import { IconName } from '@lib/components/Modal/enums';
import useActions from 'routes/PublicApi/utils/useActions';
import ApiTokenList from 'routes/PublicApi/components/ApiTokenList/ApiTokenList';
import HowToUseApiModal from './components/HowToUseApiModal/HowToUseApiModal';

const modals = [
  Modals.HowToUseApi,
  Modals.ApiTokenFormModal,
  Modals.ApiTokenDestroyModal,
];

export default function PublicApi() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [createdApiToken, setCreatedApiToken] = useState<ApiToken | null>(null);
  const useBulkSelectResult = useTableItemsBulkSelect<ApiToken>();
  const useModalManagementResult = useModalManagement(modals, () =>
    useBulkSelectResult.handleSelectSingleTableItem(null),
  );
  const useActionsResult = useActions({
    useModalManagementResult,
    useBulkSelectResult,
    setCreatedApiToken,
  });

  const { isModalOpened } = useModalManagementResult;
  const {
    apiTokenCreateMutationResult,
    apiTokenQuery,
    apiTokenUpdateMutationResult,
    apiTokensQuery,
    onCloseAddApiTokenModal,
    onCloseDestroyApiTokenModal,
    onCloseHowToUseApiModal,
    onConfirmedDelete,
    onSubmitApiTokenForm,
  } = useActionsResult;

  return (
    <>
      <BreadCrumbs data={getBreadCrumbsDataByKey({ pathname, t })} />
      {apiTokensQuery.error ? (
        <ErrorPage
          status={getStatusFromError(apiTokensQuery.error)}
          description={apiTokensQuery.error?.message}
        />
      ) : (
        <ApiTokenList
          useActionsResult={useActionsResult}
          useModalManagementResult={useModalManagementResult}
        />
      )}
      <HowToUseApiModal
        isOpen={isModalOpened(Modals.HowToUseApi)}
        onClose={onCloseHowToUseApiModal}
      />
      <ApiTokenModalForm
        isOpen={isModalOpened(Modals.ApiTokenFormModal)}
        onClose={onCloseAddApiTokenModal}
        graphQLErrors={
          apiTokenCreateMutationResult.error?.graphQLErrors ||
          apiTokenUpdateMutationResult.error?.graphQLErrors
        }
        onSubmit={onSubmitApiTokenForm}
        loading={
          apiTokenQuery.loading ||
          apiTokenCreateMutationResult.loading ||
          apiTokenUpdateMutationResult.loading
        }
        createdApiToken={createdApiToken}
        selectedApiToken={
          apiTokenQuery.data || useBulkSelectResult.selectedSingleTableItem
        }
        error={apiTokenQuery.error}
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-api-token-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.ApiTokenDestroyModal)}
        onClose={onCloseDestroyApiTokenModal}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
      />
    </>
  );
}
